import { useState, useCallback, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { TranslationKeys } from 'app/translations';
import { useSnakeBar } from 'app/hooks';
import { useBreadcrumb } from 'app/components/Breadcrumbs';
import useLocale from 'app/components/Intl/useLocale';
import { useFileUploadCleanupPreviewMutation, useFileUploadCleanupExecuteMutation } from 'app/hooks/api/admin';
import type { FileUploadCleanupPreviewResponse } from '../../Services';
import { buildPreviewGroups, noFilesFoundForDate, totalRecords } from './types';

export const useFileUploadCleanupPage = () => {
  const intl = useIntl();
  const { locale } = useLocale();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();

  useBreadcrumb(TranslationKeys.menu_adminPanel_undoUploadedFile);

  // ── Form state ─────────────────────────────────────────────────────────────
  const [connectionId, setConnectionId] = useState('');
  const [sinceDateValue, setSinceDateValue] = useState<Date | null>(null);
  const [previewData, setPreviewData] = useState<FileUploadCleanupPreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const [isCountdownActive, setIsCountdownActive] = useState(false);

  const { mutateAsync: previewCleanup, isLoading: isLoadingPreview } = useFileUploadCleanupPreviewMutation();
  const { mutateAsync: executeCleanup } = useFileUploadCleanupExecuteMutation();

  const isSinceDateValid = sinceDateValue !== null && !Number.isNaN(sinceDateValue.getTime());
  const isFormValid = connectionId.trim().length > 0 && isSinceDateValid;
  const sinceDateISO = isSinceDateValid ? sinceDateValue!.toISOString() : '';

  // ── Preview ────────────────────────────────────────────────────────────────
  const handlePreview = useCallback(async () => {
    setPreviewError(null);
    setPreviewData(null);
    setIsConfirmed(false);
    try {
      const data = await previewCleanup({ connectionId: connectionId.trim(), sinceDate: sinceDateISO });
      setPreviewData(data);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ??
        err?.message ??
        intl.formatMessage({ id: TranslationKeys.adminPanel_undoUploadedFile_previewError });
      setPreviewError(message);
    }
  }, [connectionId, sinceDateISO, previewCleanup, intl]);

  // ── Execute ────────────────────────────────────────────────────────────────
  const handleExecute = useCallback(async () => {
    if (!previewData) return;
    setIsDeleting(true);
    try {
      await executeCleanup({ connectionId: connectionId.trim(), sinceDate: sinceDateISO });
      const total = totalRecords(previewData);
      showSuccessSnakeBar({
        method: 'undoUploadedFileExecute',
        message: intl.formatMessage({ id: TranslationKeys.adminPanel_undoUploadedFile_executeSuccess }, { total }),
      });
      setConnectionId('');
      setSinceDateValue(null);
      setPreviewData(null);
      setIsConfirmed(false);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ??
        err?.message ??
        intl.formatMessage({ id: TranslationKeys.adminPanel_undoUploadedFile_executeError });
      showErrorSnakeBar({ method: 'undoUploadedFileExecute', message });
    } finally {
      setIsDeleting(false);
    }
  }, [connectionId, sinceDateISO, previewData, executeCleanup, intl, showSuccessSnakeBar, showErrorSnakeBar]);

  const previewGroups = useMemo(() => (previewData ? buildPreviewGroups(previewData) : []), [previewData]);
  const hasNoFilesForDate = previewData ? noFilesFoundForDate(previewData) : false;
  const lastUploadedFileDetails = previewData?.lastUploadedFileDetails ?? null;

  // ── Countdown timer for confirmation button ────────────────────────────────
  useEffect(() => {
    if (!isConfirmed) {
      setCountdownSeconds(0);
      setIsCountdownActive(false);
      return;
    }

    setCountdownSeconds(3);
    setIsCountdownActive(true);

    const interval = setInterval(() => {
      setCountdownSeconds(prev => {
        if (prev <= 1) {
          setIsCountdownActive(false);
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isConfirmed]);

  return {
    locale,
    connectionId,
    setConnectionId,
    sinceDateValue,
    setSinceDateValue,
    previewData,
    previewError,
    isConfirmed,
    setIsConfirmed,
    isDeleting,
    isLoadingPreview,
    countdownSeconds,
    isCountdownActive,
    isFormValid,
    sinceDateISO,
    previewGroups,
    hasNoFilesForDate,
    lastUploadedFileDetails,
    handlePreview,
    handleExecute,
  };
};
